import { SkeletonBar, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type InputHTMLAttributes, type ReactNode, useId } from 'react';

import type { AutoshipDiscountMessages } from '../lib';
import './autoship-purchase-options.scss';

export type PurchaseOption = 'one-time' | 'autoship';

type PurchaseOptionInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'id' | 'value' | 'className' | 'disabled'
>;

type AutoshipPurchaseOptionsProps = Readonly<{
    selected: PurchaseOption;
    inputProps: PurchaseOptionInputProps;
    isAutoshipDisabled: boolean;
    isLoading: boolean;
    messages: AutoshipDiscountMessages;
    footnote?: string;
    oneTimePriceSlot?: ReactNode;
    infoSlot?: ReactNode;
    children?: ReactNode;
    testId?: string;
}>;

type PurchaseOptionCardProps = Readonly<{
    id: string;
    value: PurchaseOption;
    isChecked: boolean;
    inputProps: PurchaseOptionInputProps;
    disabled?: boolean;
    className?: string;
    children: ReactNode;
}>;

const PurchaseOptionCard = ({
    id,
    value,
    isChecked,
    inputProps,
    disabled,
    className,
    children,
}: PurchaseOptionCardProps) => (
    <label
        className={cn('purchase-options__option', className, { 'purchase-options__option--checked': isChecked })}
        htmlFor={id}
    >
        {children}
        <input className="radio" type="radio" value={value} id={id} {...inputProps} disabled={disabled} />
    </label>
);

export const AutoshipPurchaseOptions = ({
    selected,
    inputProps,
    isAutoshipDisabled,
    isLoading,
    messages,
    footnote,
    oneTimePriceSlot,
    infoSlot,
    children,
    testId = 'pdp-purchase-options',
}: AutoshipPurchaseOptionsProps) => {
    const idPrefix = useId();
    const isAutoshipSelected = selected === 'autoship';

    return (
        <fieldset className="purchase-options" data-testid={testId}>
            <PurchaseOptionCard
                id={`${idPrefix}-one-time`}
                value="one-time"
                isChecked={selected === 'one-time'}
                inputProps={inputProps}
            >
                <div className="purchase-options__option--title-container">
                    <TextPassage className="purchase-options__option--title" size="lg">
                        One-time purchase
                    </TextPassage>
                    {oneTimePriceSlot}
                </div>
            </PurchaseOptionCard>
            <PurchaseOptionCard
                id={`${idPrefix}-autoship`}
                value="autoship"
                isChecked={isAutoshipSelected}
                inputProps={inputProps}
                disabled={isAutoshipDisabled}
                className="purchase-options__option--autoship"
            >
                <div className="purchase-options__option--autoship-main">
                    <div className="purchase-options__option--body">
                        <div className="purchase-options__option--title-container">
                            <TextPassage className="purchase-options__option--title" size="lg">
                                {isLoading ? (
                                    <SkeletonBar className="purchase-options__option--title-skeleton" />
                                ) : (
                                    messages.autoshipDiscountHeader
                                )}
                            </TextPassage>
                            {infoSlot}
                        </div>
                        <div className="purchase-options__option--content">
                            <div className="purchase-options__option--content-text">
                                {isLoading ? (
                                    <SkeletonBar className="purchase-options__option--content-text-skeleton" />
                                ) : (
                                    <>
                                        <TextPassage>{messages.autoshipDiscountSubtext}</TextPassage>
                                        {messages.autoshipDiscountSubtextTwo && (
                                            <TextPassage>{messages.autoshipDiscountSubtextTwo}</TextPassage>
                                        )}
                                        {footnote && <TextPassage size="sm">{footnote}</TextPassage>}
                                    </>
                                )}
                            </div>
                            {isAutoshipSelected && children}
                        </div>
                    </div>
                </div>
            </PurchaseOptionCard>
        </fieldset>
    );
};
